import { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import RoleBasedLayout, {
  type LayoutMenuItem,
} from "../components/RoleBasedLayout";
import {
  LayoutDashboard,
  Home,
  Building,
  BookOpen,
  BarChart3,
  DollarSign,
  FileText,
  Users,
  Headphones,
  Mail,
} from "lucide-react";
import Dashboard from "./super-admin/Dashboard";
import Hostels from "./super-admin/Hostels";
import Institutions from "./super-admin/Institutions";
import InstitutionDetail from "./super-admin/InstitutionDetail";
import AddInstitution from "./super-admin/AddInstitution";
import Scholarships from "./super-admin/Scholarships";
import Reports from "./super-admin/Reports";
import Funding from "./super-admin/Funding";
import UserManagement from "./super-admin/UserManagement";
import Crm from "./super-admin/CRM";
import ContactQueries from "./super-admin/ContactQueries";
import Podcasts from "./super-admin/cms/Podcasts";
import Events from "./super-admin/cms/Events";
import Blogs from "./super-admin/cms/Blogs";
import MediaGallery from "./super-admin/cms/MediaGallery";
import PressCoverage from "./super-admin/cms/PressCoverage";

const superAdminMenuItems: LayoutMenuItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard/super-admin" },
  { icon: Home, label: "Hostels", path: "/super-admin/hostels" },
  { icon: Building, label: "Institutions", path: "/super-admin/institutions" },
  { icon: BookOpen, label: "Scholarships", path: "/super-admin/scholarships" },
  { icon: BarChart3, label: "Reports", path: "/super-admin/reports" },
  { icon: DollarSign, label: "Funding Status", path: "/super-admin/funding" },
  {
    icon: FileText,
    label: "CMS Pages",
    path: "/super-admin/cms",
    subItems: [
      { label: "Podcasts", path: "/super-admin/cms/podcasts" },
      { label: "Events", path: "/super-admin/cms/events" },
      { label: "Blogs", path: "/super-admin/cms/blogs" },
      { label: "Media Gallery", path: "/super-admin/cms/media" },
      { label: "Press Coverage", path: "/super-admin/cms/press" },
    ],
  },
  {
    icon: Users,
    label: "User Management",
    path: "/super-admin/user-management",
  },
  { icon: Headphones, label: "CRM Module", path: "/super-admin/crm" },
  { icon: Mail, label: "Contact Us", path: "/super-admin/contact" },
];

export default function SuperAdminDashboard() {
  const { userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();

  useEffect(() => {
    // Only redirect if loading is complete and role doesn't match
    if (!loading && userRole && userRole !== "super_admin") {
      navigate("/portal-selection");
    }
  }, [userRole, loading, navigate]);

  return (
    <RoleBasedLayout
      menuItems={superAdminMenuItems}
      sidebarTitle="Super Admin"
      sidebarSubtitle="Control Panel"
      signOutRedirect="/"
      avatarFallback="SA"
    >
      <Routes>
        {/* Routes are relative to /super-admin/* parent route */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/hostels" element={<Hostels />} />
        <Route path="/institutions" element={<Institutions />} />
        <Route path="/institutions/:id" element={<InstitutionDetail />} />
        <Route path="/institutions/add" element={<AddInstitution />} />
        <Route path="/scholarships" element={<Scholarships />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/funding" element={<Funding />} />
        <Route path="/user-management" element={<UserManagement />} />
        <Route path="/crm" element={<Crm />} />
        <Route path="/contact" element={<ContactQueries />} />
        <Route path="/cms/podcasts" element={<Podcasts />} />
        <Route path="/cms/events" element={<Events />} />
        <Route path="/cms/blogs" element={<Blogs />} />
        <Route path="/cms/media" element={<MediaGallery />} />
        <Route path="/cms/press" element={<PressCoverage />} />
      </Routes>
    </RoleBasedLayout>
  );
}
